import React, { Fragment, useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Button, ConfirmationModal, LoadingSpinner, EventReallocationAccessModal, AreaAccessModal } from '../components'
import { UserFormModal } from '../components/UserFormModal'
import { ConfirmationModalMeta, DeleteUserMeta, LoadingSpinnerMeta, ROLE_MAP, UpdateUserFormMeta, UserFormModalMeta } from '../data'
import type { Role, UserMetaProps } from '../data/types'
import { User } from '../data/types'
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, DeleteIcon, EditIcon, SearchIcon, LocationIcon } from '../icons'
import { useAuth } from './auth/AuthContext'
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const ITEMS_PER_PAGE = 10

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

type Props = {
    meta: UserMetaProps
}
export const Users = (props: Props) => {
    const { meta } = props
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [users, setUser] = useState<Array<any>>([])
    const { user } = useAuth()
    const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)
    const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)
    const [isEventAccessModalOpen, setIsEventAccessModalOpen] = useState(false)
    const [isAreaAccessModalOpen, setIsAreaAccessModalOpen] = useState(false)
    const [isDeletingUser, setIsDeletingUser] = useState(false)
    const [selectedUser, setSelectedUser] = useState<User | null>(null)


    // Pagination state
    const [loadingPage, setLoadingPage] = useState<number>(1)
    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    })

    // Filter state
    const [nameSearch, setNameSearch] = useState('')
    const [emailSearch, setEmailSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState<string>('')

    // Applied filters (what's actually sent to the server)
    const [appliedFilters, setAppliedFilters] = useState({
        name: '',
        email: '',
        role: ''
    })

    const hasFetched = useRef(false)

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true
            fetchUser(1, appliedFilters)
        }
    }, [])

    const buildQueryString = (page: number, filters: typeof appliedFilters) => {
        const params = new URLSearchParams()
        params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('page', page.toString())


        if (filters.name) params.append('name', filters.name)
        if (filters.email) params.append('email', filters.email)
        if (filters.role) params.append('role', filters.role)

        return params.toString()
    }


    const fetchUser = async (page: number, filters: typeof appliedFilters) => {
        setLoadingPage(page)
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page, filters)
            const response = await fetch(`${API_BASE_URL}/users?${queryString}`, { credentials: 'include' })
            if (!response.ok) {
                const text = await response.text()
                const textObj = JSON.parse(text)
                throw new Error(`${textObj.message}`)
            }
            const result = await response.json()

            setUser(result.data || [])
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            })
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching users')
        }
        finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        const filters = {
            name: nameSearch.trim(),
            email: emailSearch.trim(),
            role: roleFilter
        }
        setAppliedFilters(filters)
        fetchUser(1, filters)
    }

    const handleClear = () => {
        setNameSearch('')
        setEmailSearch('')
        setRoleFilter('')
        const emptyFilters = { name: '', email: '', role: '' }
        setAppliedFilters(emptyFilters)
        fetchUser(1, emptyFilters)
    }

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchUser(page, appliedFilters)
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }


    const handleToggleAdmin = async (value: boolean, users: any) => {
        setUpdatingUserId(value ? users.id : null);
        setError(null);
        try {
            const id = users.id;
            const newActiveStatus = value;
            const volunteerIdValue = newActiveStatus === true ? user?.volunteer_csid : null;
            const outh_provider = newActiveStatus === true ? "custom_oauth" : null;

            const allRequiredUserData = {
                name: users.name,
                email: users.email,
                oauth_id: volunteerIdValue,
                oauth_provider: outh_provider,
                username: users.username,
                volunteer_csid: users.volunteer_csid,
            };


            const response = await fetch(API_BASE_URL + '/user/' + id, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'include',
                body: JSON.stringify(allRequiredUserData),
            });
            if (!response.ok) {
                const text = await response.text();
                const textObj = JSON.parse(text);
                throw new Error(`${textObj.message}`);
            }
            const result = await response.json();
            console.log(result);

            setUser(prevUsers =>
                prevUsers.map(u =>
                    u.id === id
                        ? {
                            ...u,
                            oauth_id: volunteerIdValue,
                            oauth_provider: outh_provider
                        }
                        : u
                )
            );

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while updating admin status');
        } finally {

            setUpdatingUserId(null);
        }
    }

    const handleUpdateUser = async (userData: User) => {
        if (!selectedUser) return

        const response = await fetch(`${API_BASE_URL}/user/${selectedUser.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(userData)
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        if (result.model) {
            // Refetch the current page after update
            fetchUser(pagination.current_page, appliedFilters)
        }

    }

    const handleDeleteUserConfirm = async () => {
        if (!selectedUser) return

        setIsDeletingUser(true)
        try {
            const response = await fetch(`${API_BASE_URL}/user/${selectedUser.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include',
            })

            if (!response.ok) {
                const text = await response.text()
                const errorText = JSON.parse(text)
                throw new Error(errorText.message);
            }

            // Refetch the current page after deletion
            fetchUser(pagination.current_page, appliedFilters)
            setIsDeleteUserModalOpen(false)
            setSelectedUser(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete User')
            setIsDeleteUserModalOpen(false)
        } finally {
            setIsDeletingUser(false)
        }
    }

    const handleEditUserClick = (user: User) => {
        setSelectedUser(user)
        setIsEditUserModalOpen(true)
    }
    const handleDeleteUserClick = (user: User) => {
        setSelectedUser(user)
        setIsDeleteUserModalOpen(true)
    }
    const handleEventAccessClick = (user: User) => {
        setSelectedUser(user)
        setIsEventAccessModalOpen(true)
    }
    const handleAreaAccessClick = (user: User) => {
        setSelectedUser(user)
        setIsAreaAccessModalOpen(true)
    }
    const handleDeleteUserCancel = () => {
        setIsDeleteUserModalOpen(false)
        setSelectedUser(null)
    }


    return (

        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>
                    </div>

                    {/* Search Filters */}
                    <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            <input
                                type="text"
                                placeholder="Search by name..."
                                value={nameSearch}
                                onChange={(e) => setNameSearch(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                            <input
                                type="text"
                                placeholder="Search by email..."
                                value={emailSearch}
                                onChange={(e) => setEmailSearch(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">All Roles</option>
                                {Object.entries(ROLE_MAP).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Search Buttons */}
                        <div className="flex gap-2">
                            <Button onClick={handleSearch} size="sm">
                                <SearchIcon className="w-4 h-4 me-2" />
                                Search
                            </Button>
                            <Button onClick={handleClear} variant="outline" size="sm">
                                Clear
                            </Button>
                        </div>

                        {error && (
                            <div className="mt-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg">
                                {error}
                            </div>
                        )}

                        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                            Showing {pagination.total} user{pagination.total !== 1 ? 's' : ''}
                        </div>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    <div className="flex justify-center">
                        {!loading && (
                            <table className="min-w-full bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
                                <thead className="bg-gray-200 dark:bg-gray-700">
                                    <tr>
                                        <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">User ID</th>
                                        <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Full Name</th>
                                        <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Email</th>
                                        <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Role</th>
                                        <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Action</th>
                                        <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Access Level</th>
                                    </tr>
                                </thead>
                                <tbody>

                                    {users.map((userData, index) => (
                                        <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                                            <td className="px-6 text-gray-900 dark:text-gray-100">{userData.id}</td>
                                            <td className="px-6 text-gray-900 dark:text-gray-100">{userData.name}</td>
                                            <td className="px-6 text-gray-900 dark:text-gray-100">{userData.email}</td>
                                            <td className="px-6 text-gray-900 dark:text-gray-100">{userData.role}</td>
                                            <td className="px-6 text-gray-900 dark:text-gray-100">
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        onClick={() => { handleEditUserClick(userData) }}
                                                        variant="icon"
                                                        size="sm"
                                                        title="Edit user">
                                                        <EditIcon className="w-5 h-5" />
                                                    </Button>

                                                    <Button
                                                        onClick={() => { handleEventAccessClick(userData) }}
                                                        variant="icon"
                                                        size="sm"
                                                        title="Manage event reallocation access">
                                                        <CalendarIcon className="w-5 h-5" />
                                                    </Button>

                                                    <Button
                                                        onClick={() => { handleAreaAccessClick(userData) }}
                                                        variant="icon"
                                                        size="sm"
                                                        title="Manage area access">
                                                        <LocationIcon className="w-5 h-5" />
                                                    </Button>

                                                    <Button
                                                        onClick={() => { handleDeleteUserClick(userData) }}
                                                        variant="icon"
                                                        size="sm"
                                                        title="Delete user">
                                                        <DeleteIcon className="w-5 h-5" />
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 text-gray-900 dark:text-gray-100 text-start">
                                                <button
                                                    onClick={() => handleToggleAdmin(!userData.oauth_id, userData)}
                                                    disabled={updatingUserId === userData.id}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${userData.oauth_id ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                                                        } ${updatingUserId === userData.id ? 'opacity-50 cursor-not-allowed' : ''}
                                                `}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userData.oauth_id ? 'translate-x-6' : 'translate-x-1'
                                                            }`}
                                                    />
                                                </button>
                                            </td>

                                        </tr>
                                    ))}

                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {!loading && pagination.last_page > 1 && (
                        <div className="flex justify-center items-center gap-4 mt-6">
                            <Button
                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                disabled={pagination.current_page === 1 || loading}
                                variant="outline"
                                size="sm"
                            >
                                <ChevronLeftIcon className="w-5 h-5" />
                            </Button>

                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Page {pagination.current_page} of {pagination.last_page}
                            </span>

                            <Button
                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                disabled={pagination.current_page === pagination.last_page || loading}
                                variant="outline"
                                size="sm"
                            >
                                <ChevronRightIcon className="w-5 h-5" />
                            </Button>
                        </div>
                    )}
                </div>

            </div>
            <UserFormModal
                meta={{ ...UserFormModalMeta, ...UpdateUserFormMeta }}
                isOpen={isEditUserModalOpen}
                onClose={() => {
                    setIsEditUserModalOpen(false)
                    setSelectedUser(null)
                }}
                onSubmit={handleUpdateUser}
                initialuser={selectedUser}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteUserModalOpen}
                onConfirm={handleDeleteUserConfirm}
                onCancel={handleDeleteUserCancel}
                title={DeleteUserMeta.confirmTitle}
                message={DeleteUserMeta.confirmMessage}
                isLoading={isDeletingUser}
            />

            <EventReallocationAccessModal
                isOpen={isEventAccessModalOpen}
                onClose={() => {
                    setIsEventAccessModalOpen(false)
                    setSelectedUser(null)
                }}
                user={selectedUser}
            />

            <AreaAccessModal
                isOpen={isAreaAccessModalOpen}
                onClose={() => {
                    setIsAreaAccessModalOpen(false)
                    setSelectedUser(null)
                }}
                user={selectedUser}
            />
        </Fragment>
    )
};
